import {
  type F2fTransactionStatus,
  GetTransactionsByPageBody2,
  GetTransactionsByPageBody2Tx_type,
  type IGetTransactionsByPageBody2,
  type ITransaction,
  type OnlineTransactionStatus,
  Query,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { GridPaginationModel } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import axios, { type CancelTokenSource } from "axios";
import { useSnackbar } from "notistack";
import { type RefObject, useCallback, useMemo, useRef, useState } from "react";

import { ReadableDataGrid } from "~/components/readable_data_grid";
import { SegmentedToggle } from "~/components/segmented_toggle";
import {
  Select2,
  Select2ApplyBar,
  Select2TextPopover,
} from "~/components/select2";
import { useConfig } from "~/contexts/config";
import { type CurrencyOption, useCurrencies } from "~/services/money";
import { downloadCsv, initStreamingExport } from "~/utilities/csv";
import { renderMoneyWithoutCurrencySymbol } from "~/utilities/money";
import type { PathKeys } from "~/utilities/object";
import { getValueByPath } from "~/utilities/object";

import { CurrencyFilterPopover } from "./CurrencyFilterPopover";
import { DateRangePopover } from "./DateRangePopover";
import { PriceRangePopover } from "./PriceRangePopover";
import { StatusFilterPopover } from "./StatusFilterPopover";
import { useCreateTransactionColumns } from "./TransactionColumns";
import {
  formatDateRangeForChip,
  formatPriceRangeForChip,
  mapStatusOptions,
  parsePriceFilter,
} from "./transactionFilterUtils";
import { useExcludeStatus } from "./useExcludeStatus";

type FormValues = Omit<
  IGetTransactionsByPageBody2,
  "total_price_from" | "total_price_to"
> & {
  total_price_from?: string;
  total_price_to?: string;
};

type ExportTransactionColumns = PathKeys<ITransaction>;

const EXPORT_TRANSACTIONS_PAGE_SIZE = 100;
const EXPORT_TRANSACTIONS_CONCURRENCY = 10;
const EXPORT_TRANSACTIONS_WAIT_MESSAGE_COUNT_THRESHOLD = 1000;
const EXPORT_TRANSACTIONS_DEFAULT_FILE_NAME = "transactions.csv";

const EXPORT_TRANSACTIONS_COLUMNS: ExportTransactionColumns[] = [
  "id",
  "status",
  "description",
  "currency",
  "created",
  "buyer_id",
  "seller_id",
  "joined",
  "cancelled",
  "amount_refunded",
  "amount_released",
  "order_issue_raised",
  "funds_released",
  "complained",
  "complaint_period_deadline",
  "complaint_period_ended",
  "client_id",
  "claimed_by_buyer",
  "tracked",
  "delivered",
  "cancellation_reason",
];

const EXPORT_TRANSACTIONS_COLUMNS_F2F: ExportTransactionColumns[] = [
  "skip_remainder",
  "rejected",
  "deposit_review_flagged",
  "deposit_paid",
  "deposit_pricing.price",
  "deposit_pricing.price_extra",
  "deposit_pricing.charge",
  "deposit_pricing.charge_international_payment",
  "deposit_pricing.charge_seller",
  "deposit_pricing.charge_buyer_client",
  "deposit_pricing.charge_seller_client",
  "deposit_pricing.charge_buyer_service",
  "deposit_pricing.charge_seller_service",
  "deposit_pricing.extra_processing_charge",
  "deposit_pricing.postage_processing_charge",
  "deposit_accepted",
  "deposit_refunded",
  "refunded",
  "remainder_skipped",
  "priced",
  "remainder_review_flagged",
  "remainder_paid",
  "buyer_handover_confirmed",
  "seller_handover_confirmed",
  "claimed_by_seller",
  "buyer_is_guest",
  "seller_is_guest",
  "client_accrual_net",
  "payout_id",
  "shopify_order_id",
  "shopify_order_name",
  "shopify_order_url",
];

const EXPORT_TRANSACTIONS_COLUMNS_ONLINE: ExportTransactionColumns[] = [
  "price",
  "charge",
  "charge_seller",
  "charge_buyer_client",
  "charge_seller_client",
  "charge_international_payment",
  "charge_postage_buyer",
  "charge_postage_client",
  "listing_creator_accepted",
  "listing_creator_rejected",
  "review_flagged",
  "paid",
  "payment_refunded",
];

const EXPORT_TRANSACTIONS_COLUMNS_REMAPPING: Partial<
  Record<ExportTransactionColumns, ExportTransactionColumns>
> = {
  id: "v2_id",
};

const MONEY_FIELDS = new Set<ExportTransactionColumns>([
  "price",
  "charge",
  "charge_seller",
  "charge_buyer_client",
  "charge_seller_client",
  "charge_international_payment",
  "charge_postage_buyer",
  "charge_postage_client",
  "amount_refunded",
  "amount_released",
  "client_accrual_net",
  "deposit_pricing.price",
  "deposit_pricing.price_extra",
  "deposit_pricing.charge",
  "deposit_pricing.charge_seller",
  "deposit_pricing.charge_buyer_client",
  "deposit_pricing.charge_seller_client",
  "deposit_pricing.charge_buyer_service",
  "deposit_pricing.charge_seller_service",
  "deposit_pricing.charge_international_payment",
  "deposit_pricing.extra_processing_charge",
  "deposit_pricing.postage_processing_charge",
  "pricing.price",
  "pricing.price_extra",
  "pricing.charge",
  "pricing.charge_seller",
  "pricing.charge_buyer_client",
  "pricing.charge_seller_client",
  "pricing.charge_buyer_service",
  "pricing.charge_seller_service",
  "pricing.charge_international_payment",
  "pricing.extra_processing_charge",
  "pricing.postage_processing_charge",
]);

const formatTransactionsToCsvRows = (
  transactions: ITransaction[],
  exportColumns: ExportTransactionColumns[],
  remappedColumns: Partial<
    Record<ExportTransactionColumns, ExportTransactionColumns>
  >,
  locale: string,
) => {
  return transactions.map((transaction) => {
    return exportColumns.map((col) => {
      let rawValue = getValueByPath(transaction, col);
      const remappedCol = remappedColumns[col];
      if (remappedCol !== undefined) {
        if (getValueByPath(transaction, remappedCol) !== undefined) {
          rawValue = getValueByPath(transaction, remappedCol);
          col = remappedCol satisfies ExportTransactionColumns;
        }
      }
      if (rawValue instanceof Date) {
        return rawValue.toISOString();
      }
      if (typeof rawValue === "number" && MONEY_FIELDS.has(col)) {
        return renderMoneyWithoutCurrencySymbol(
          rawValue,
          transaction.currency,
          locale,
        );
      }
      return rawValue;
    });
  });
};

const getClientPage = (
  page: number,
  pageSize: number,
  clientId: string,
  buildFiltersFromSearch: FormValues,
  locale: string,
  exportColumns: ExportTransactionColumns[],
  remappedColumns: Partial<
    Record<ExportTransactionColumns, ExportTransactionColumns>
  >,
  buffer: Record<number, unknown[][]>,
  exportStateRef: RefObject<{
    cancelRequested: boolean;
    writable?: FileSystemWritableFileStream;
  }>,
  cancelSourcesRef: RefObject<Map<number, CancelTokenSource>>,
  onPageArrive?: () => Promise<void>,
): Promise<void> => {
  if (exportStateRef.current.cancelRequested) {
    return Promise.resolve();
  }
  const fromJSBody: Record<string, unknown> = {
    ...buildFiltersFromSearch,
    current_page: page,
    page_size: pageSize,
  };
  if (buildFiltersFromSearch.total_price_from) {
    fromJSBody.total_price_from = parsePriceFilter(
      buildFiltersFromSearch.total_price_from,
      buildFiltersFromSearch.currency || "",
      locale,
    );
  }
  if (buildFiltersFromSearch.total_price_to) {
    fromJSBody.total_price_to = parsePriceFilter(
      buildFiltersFromSearch.total_price_to,
      buildFiltersFromSearch.currency || "",
      locale,
    );
  }
  const requestBody = GetTransactionsByPageBody2.fromJS(fromJSBody);
  const source = axios.CancelToken.source();
  cancelSourcesRef.current.set(page, source);
  const p = Query.Client()
    .core_getTransactionsByPage(clientId, requestBody, source.token)
    .then(async (resp: { data: ITransaction[] }) => {
      if (exportStateRef.current.cancelRequested) {
        buffer[page] = [];
        return;
      }
      if (resp.data) {
        buffer[page] = formatTransactionsToCsvRows(
          resp.data,
          exportColumns,
          remappedColumns,
          locale,
        );
      } else {
        buffer[page] = [];
      }
      await onPageArrive?.();
    })
    .catch(async () => {
      buffer[page] = [];
      await onPageArrive?.();
    })
    .finally(() => {
      const src = cancelSourcesRef.current.get(page);
      if (src) {
        cancelSourcesRef.current.delete(page);
      }
    }) as Promise<void>;
  return p;
};

interface TxTypePopoverProps {
  initialValue: GetTransactionsByPageBody2Tx_type;
  onApply: (next: GetTransactionsByPageBody2Tx_type) => void;
  onClose: () => void;
}

function TxTypePopover({ initialValue, onApply, onClose }: TxTypePopoverProps) {
  const [value, setValue] =
    useState<GetTransactionsByPageBody2Tx_type>(initialValue);
  const apply = () => {
    onApply(value);
    onClose();
  };
  return (
    <Stack>
      <Stack sx={{ p: 1 }}>
        <SegmentedToggle
          value={value}
          onChange={setValue}
          options={[
            { value: GetTransactionsByPageBody2Tx_type.F2f, label: "F2F" },
            {
              value: GetTransactionsByPageBody2Tx_type.Online,
              label: "Online",
            },
          ]}
          ariaLabel="Transaction type"
        />
      </Stack>
      <Select2ApplyBar onApply={apply} />
    </Stack>
  );
}

export function Transactions() {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const defaultPageSize = isLgDown ? 10 : 25;

  const clientId = useParams({
    from: "/clients/$clientId/transactions",
    select(params) {
      return params.clientId;
    },
  });

  const getTransactionUrl = (row: ITransaction) =>
    `/clients/${clientId}/${row.type}/transactions/${row.id}`;

  const [isExporting, setIsExporting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { data: statusData } = Query.useCore_getTransactionStatusesQuery();
  const f2fStatuses = statusData?.f2f;
  const onlineStatuses = statusData?.online;
  const f2fStatusOptions = useMemo(
    () =>
      mapStatusOptions(
        f2fStatuses?.filter((s: F2fTransactionStatus) => !s.deprecated),
      ),
    [f2fStatuses],
  );
  const onlineStatusOptions = useMemo(
    () =>
      mapStatusOptions(
        onlineStatuses?.filter((s: OnlineTransactionStatus) => !s.deprecated),
      ),
    [onlineStatuses],
  );
  const { localization } = useConfig();
  const locale = navigator.languages?.[0] || localization.locale;

  const { currencies } = useCurrencies();

  const search = useSearch({ from: "/clients/$clientId/transactions" });
  const navigate = useNavigate({ from: "/clients/$clientId/transactions" });

  const txType = search.tx_type;

  const updateSearch = useCallback(
    (partial: Partial<typeof search>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          ...partial,
          page: 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const allStatuses = useMemo(
    () => [...f2fStatusOptions, ...onlineStatusOptions],
    [f2fStatusOptions, onlineStatusOptions],
  );

  const currentStatuses = useMemo(
    () => (txType === "f2f" ? f2fStatusOptions : onlineStatusOptions),
    [txType, f2fStatusOptions, onlineStatusOptions],
  );

  const statusFieldKey =
    txType === "f2f" ? "f2f_txs_status" : "online_txs_status";
  const selectedStatusCodes = useMemo<string[]>(
    () =>
      (txType === "f2f" ? search.f2f_txs_status : search.online_txs_status) ??
      [],
    [txType, search.f2f_txs_status, search.online_txs_status],
  );

  const [statusMode, setStatusMode] = useState<"include" | "exclude">(
    "include",
  );

  const {
    excludeStatusValue,
    clearExcludeStatus,
    resetExcludeStatus,
    isExcludeStatusCleared,
  } = useExcludeStatus(navigate, search.exclude_status);

  const createTransactionColumns = useCreateTransactionColumns();
  const columns = useMemo(
    () => createTransactionColumns(allStatuses, localization),
    [allStatuses, createTransactionColumns, localization],
  );

  const buildFiltersFromSearch = useMemo<FormValues>(
    () => ({
      tx_type: search.tx_type,
      current_page: search.page,
      page_size: search.pageSize,
      exclude_status: isExcludeStatusCleared
        ? search.exclude_status || undefined
        : (search.exclude_status ?? "cancelled"),
      currency: search.currency || "",
      tx_id: search.tx_id,
      total_price_from: search.total_price_from,
      total_price_to: search.total_price_to,
      descr: search.descr,
      email: search.email,
      date_created_from: search.date_created_from
        ? new Date(search.date_created_from)
        : undefined,
      date_created_to: search.date_created_to
        ? new Date(search.date_created_to)
        : undefined,
      f2f_txs_status: search.f2f_txs_status || [],
      online_txs_status: search.online_txs_status || [],
    }),
    [search, isExcludeStatusCleared],
  );

  const {
    data: transactionsData,
    isFetching: isLoadingTransactions,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: [
      "transactions",
      "core_getTransactionsByPage",
      clientId,
      buildFiltersFromSearch,
      locale,
    ],
    queryFn: async () => {
      const fromJSBody: Record<string, unknown> = {
        ...buildFiltersFromSearch,
      };
      if (buildFiltersFromSearch.total_price_from) {
        fromJSBody.total_price_from = parsePriceFilter(
          buildFiltersFromSearch.total_price_from,
          buildFiltersFromSearch.currency || "",
          locale,
        );
      }
      if (buildFiltersFromSearch.total_price_to) {
        fromJSBody.total_price_to = parsePriceFilter(
          buildFiltersFromSearch.total_price_to,
          buildFiltersFromSearch.currency || "",
          locale,
        );
      }
      const requestBody = GetTransactionsByPageBody2.fromJS(fromJSBody);
      const client = Query.Client();
      return client.core_getTransactionsByPage(clientId, requestBody);
    },
    placeholderData: keepPreviousData,
    enabled: !!clientId,
  });

  const rows = transactionsData?.data || [];

  const paginationModel = useMemo(
    () => ({
      page: search.page - 1,
      pageSize: search.pageSize,
    }),
    [search.page, search.pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      navigate({
        search: (prev) => {
          let page = model.page + 1;
          if (model.pageSize !== prev.pageSize) {
            page = 1;
          }
          return {
            ...prev,
            page: page,
            pageSize: model.pageSize,
          };
        },
        replace: true,
      });
    },
    [navigate],
  );

  const handleClearFilters = useCallback(() => {
    resetExcludeStatus();
    setStatusMode("include");
    navigate({
      search: {
        page: 1,
        pageSize: defaultPageSize,
        tx_type: GetTransactionsByPageBody2Tx_type.F2f,
      },
      replace: true,
    });
  }, [defaultPageSize, navigate, resetExcludeStatus]);

  const findCurrencyOption = useCallback(
    (code: string) => currencies.find((c: CurrencyOption) => c.code === code),
    [currencies],
  );

  const selectedCurrencyCode = search.currency || "";
  const selectCurrency = useCallback(
    (code: string) => updateSearch({ currency: code || undefined }),
    [updateSearch],
  );
  const clearCurrency = useCallback(
    () => updateSearch({ currency: undefined }),
    [updateSearch],
  );
  const currencyChipValue = useMemo(() => {
    if (!selectedCurrencyCode) {
      return undefined;
    }
    return (
      findCurrencyOption(selectedCurrencyCode)?.label ?? selectedCurrencyCode
    );
  }, [selectedCurrencyCode, findCurrencyOption]);

  const dateFromValue = search.date_created_from
    ? new Date(search.date_created_from)
    : null;
  const dateToValue = search.date_created_to
    ? new Date(search.date_created_to)
    : null;
  const setDateRange = useCallback(
    (from: Date | null, to: Date | null) =>
      updateSearch({
        date_created_from: from ? from.toISOString() : undefined,
        date_created_to: to ? to.toISOString() : undefined,
      }),
    [updateSearch],
  );
  const formatDateForChip = useCallback(
    (iso?: string) => {
      if (!iso) {
        return undefined;
      }
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) {
        return undefined;
      }
      return d.toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    },
    [locale],
  );

  const includeLabel = useMemo(() => {
    if (selectedStatusCodes.length === 0) {
      return undefined;
    }
    if (selectedStatusCodes.length === 1) {
      const opt = currentStatuses.find(
        (s) => s.code === selectedStatusCodes[0],
      );
      return opt?.label ?? selectedStatusCodes[0];
    }
    return `${selectedStatusCodes.length} selected`;
  }, [selectedStatusCodes, currentStatuses]);

  const excludeLabel = useMemo(() => {
    if (!excludeStatusValue) {
      return undefined;
    }
    return (
      currentStatuses.find((s) => s.code === excludeStatusValue)?.label ??
      excludeStatusValue
    );
  }, [excludeStatusValue, currentStatuses]);

  const statusChipValue = useMemo(() => {
    if (includeLabel && excludeLabel) {
      return `${includeLabel} · not ${excludeLabel}`;
    }
    if (includeLabel) {
      return includeLabel;
    }
    if (excludeLabel) {
      return `not ${excludeLabel}`;
    }
    return undefined;
  }, [includeLabel, excludeLabel]);

  const isStatusActive =
    selectedStatusCodes.length > 0 || excludeStatusValue !== "";

  const clearStatusFilters = useCallback(() => {
    clearExcludeStatus();
    updateSearch({
      [statusFieldKey]: undefined,
      exclude_status: undefined,
    });
  }, [clearExcludeStatus, statusFieldKey, updateSearch]);

  const setTxType = useCallback(
    (next: GetTransactionsByPageBody2Tx_type) => {
      resetExcludeStatus();
      navigate({
        search: (prev) => ({
          ...prev,
          tx_type: next,
          page: 1,
          f2f_txs_status: undefined,
          online_txs_status: undefined,
          exclude_status: undefined,
        }),
        replace: true,
      });
    },
    [navigate, resetExcludeStatus],
  );

  const activeFilterCount =
    (isStatusActive ? 1 : 0) +
    (selectedCurrencyCode ? 1 : 0) +
    (search.date_created_from || search.date_created_to ? 1 : 0) +
    (search.tx_id ? 1 : 0) +
    (search.email ? 1 : 0) +
    (search.descr ? 1 : 0) +
    (search.total_price_from !== undefined ||
    search.total_price_to !== undefined
      ? 1
      : 0);

  const rowCount = useMemo(() => {
    return transactionsData?.total_count ?? 0;
  }, [transactionsData]);

  // NOTE This is a copy of the exportDialogHeading function in the admin/Transactions.tsx file.
  const exportDialogHeading = useMemo(() => {
    if (rowCount === 1) {
      return "Export transaction";
    }
    return "Export transactions";
  }, [rowCount]);

  // NOTE This is a copy of the exportDialogMessage function in the admin/Transactions.tsx file.
  const exportDialogMessage = useMemo(() => {
    let unit = "transactions";
    if (rowCount === 1) {
      unit = "transaction";
    }
    let extra = "";
    if (rowCount > EXPORT_TRANSACTIONS_WAIT_MESSAGE_COUNT_THRESHOLD) {
      extra = " This export may take a few minutes.";
    }
    return `You’re about to export ${rowCount} ${unit}.${extra} Please do not close the window or leave the page.`;
  }, [rowCount]);

  // NOTE This is a copy of the exportStateRef in the admin/Transactions.tsx file.
  const exportStateRef = useRef<{
    cancelRequested: boolean;
    writable?: FileSystemWritableFileStream;
  }>({ cancelRequested: false });

  const cancelSourcesRef = useRef<Map<number, CancelTokenSource>>(new Map());

  // NOTE This is a copy of the handleCancelExport function in the admin/Transactions.tsx file.
  const handleCancelExport = useCallback(async () => {
    exportStateRef.current.cancelRequested = true;
    cancelSourcesRef.current.forEach((src) => {
      try {
        src.cancel("Export cancelled");
      } catch (err) {
        console.error("Couldn't cancel source:", err);
      }
    });
    cancelSourcesRef.current.clear();
    try {
      await exportStateRef.current.writable?.abort();
    } catch (err) {
      console.error("Couldn't abort writable file stream:", err);
    }
    exportStateRef.current.writable = undefined;
    setIsExporting(false);
    enqueueSnackbar("Export cancelled", { variant: "info" });
  }, [enqueueSnackbar]);

  const handleExport = useCallback(
    async (callback: () => void) => {
      if (!clientId) {
        return;
      }
      setIsExporting(true);
      let writable: FileSystemWritableFileStream | undefined;
      try {
        exportStateRef.current.cancelRequested = false;
        const exportColumns: ExportTransactionColumns[] = Array.from(
          new Set<ExportTransactionColumns>([
            ...EXPORT_TRANSACTIONS_COLUMNS,
            ...(txType === "f2f"
              ? EXPORT_TRANSACTIONS_COLUMNS_F2F
              : EXPORT_TRANSACTIONS_COLUMNS_ONLINE),
          ]),
        );
        const headers = exportColumns;

        let pageRequested = 1;
        const nextToWriteRef = { current: 1 };
        const buffer: Record<number, unknown[][]> = {};

        let onPageArrive: (() => Promise<void>) | undefined;
        if ("showSaveFilePicker" in window) {
          const init = await initStreamingExport(
            headers,
            EXPORT_TRANSACTIONS_DEFAULT_FILE_NAME,
            buffer,
            nextToWriteRef,
            exportStateRef,
          );
          writable = init.writable;
          onPageArrive = init.onPageArrive;
        }

        const activePromises: Promise<void>[] = [];
        const schedule = (page: number) => {
          if (exportStateRef.current.cancelRequested) {
            return;
          }
          const p = getClientPage(
            page,
            EXPORT_TRANSACTIONS_PAGE_SIZE,
            clientId,
            buildFiltersFromSearch,
            locale,
            exportColumns,
            EXPORT_TRANSACTIONS_COLUMNS_REMAPPING,
            buffer,
            exportStateRef,
            cancelSourcesRef,
            onPageArrive,
          ).finally(() => {
            const idx = activePromises.indexOf(p);
            if (idx >= 0) {
              activePromises.splice(idx, 1);
            }
          });
          activePromises.push(p);
        };

        const totalPages = Math.ceil(rowCount / EXPORT_TRANSACTIONS_PAGE_SIZE);
        while (
          activePromises.length < EXPORT_TRANSACTIONS_CONCURRENCY &&
          pageRequested <= totalPages
        ) {
          if (exportStateRef.current.cancelRequested) {
            break;
          }
          schedule(pageRequested);
          pageRequested++;
        }

        while (pageRequested <= totalPages) {
          if (exportStateRef.current.cancelRequested) {
            break;
          }
          await Promise.race(activePromises);
          while (
            activePromises.length < EXPORT_TRANSACTIONS_CONCURRENCY &&
            pageRequested <= totalPages
          ) {
            if (exportStateRef.current.cancelRequested) {
              break;
            }
            schedule(pageRequested);
            pageRequested++;
          }
        }

        await Promise.allSettled(activePromises);
        if (!exportStateRef.current.cancelRequested) {
          if (onPageArrive) {
            await onPageArrive();
          } else {
            downloadCsv(
              [headers, ...Object.values(buffer).flat()],
              EXPORT_TRANSACTIONS_DEFAULT_FILE_NAME,
            );
          }
        }

        if (!exportStateRef.current.cancelRequested) {
          await writable?.close();
          exportStateRef.current.writable = undefined;
          enqueueSnackbar("Successfully exported transactions", {
            variant: "success",
          });
        }
      } catch (error) {
        if (!exportStateRef.current.cancelRequested) {
          const isAbortError =
            error instanceof Error && error.name === "AbortError";
          if (!isAbortError) {
            enqueueSnackbar("Failed to export transactions", {
              variant: "error",
            });
          }
        }
        try {
          await writable?.abort();
        } catch (err) {
          console.error("Couldn't abort writable file stream:", err);
        }
        exportStateRef.current.writable = undefined;
      } finally {
        setIsExporting(false);
        if (!exportStateRef.current.cancelRequested) {
          callback();
        }
      }
    },
    [
      buildFiltersFromSearch,
      clientId,
      enqueueSnackbar,
      rowCount,
      txType,
      exportStateRef,
      locale,
    ],
  );

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
      <Stack
        spacing={1.5}
        sx={{
          backgroundColor: "primary._states.hover",
          p: { xs: 2, sm: 3, lg: 4 },
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          sx={{ columnGap: 1, rowGap: { xs: 2.5, sm: 2 } }}
        >
          <Select2
            label="Type"
            iconName="swap_horiz_outlined"
            active
            value={txType === "online" ? "Online" : "F2F"}
            renderPopover={(close) => (
              <TxTypePopover
                initialValue={txType}
                onApply={setTxType}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Transaction ID"
            iconName="swap_horiz"
            active={!!search.tx_id}
            value={search.tx_id}
            onClear={() => updateSearch({ tx_id: undefined })}
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={String(search.tx_id ?? "")}
                onApply={(v) => {
                  updateSearch({
                    tx_id: v.length > 0 ? v : undefined,
                  });
                }}
                onClose={close}
                placeholder="Transaction ID"
              />
            )}
          />
          <Select2
            label="Status"
            iconName="timeline"
            active={isStatusActive}
            value={statusChipValue}
            onClear={clearStatusFilters}
            renderPopoverHeader={() => (
              <SegmentedToggle
                value={statusMode}
                onChange={setStatusMode}
                options={[
                  { value: "include", label: "Include" },
                  { value: "exclude", label: "Exclude" },
                ]}
                ariaLabel="Status filter mode"
              />
            )}
            renderPopover={(close) => (
              <StatusFilterPopover
                mode={statusMode}
                options={currentStatuses}
                initialSelected={selectedStatusCodes}
                initialExclude={excludeStatusValue}
                onApply={(selected, exclude) => {
                  // Mark the exclude as explicitly cleared when emptied so the
                  // "cancelled" default in useExcludeStatus doesn't re-apply it.
                  if (exclude) {
                    resetExcludeStatus();
                  } else {
                    clearExcludeStatus();
                  }
                  updateSearch({
                    [statusFieldKey]: selected.length ? selected : undefined,
                    exclude_status: exclude || undefined,
                  });
                }}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Created"
            iconName="date"
            active={!!search.date_created_from || !!search.date_created_to}
            value={formatDateRangeForChip(
              search.date_created_from,
              search.date_created_to,
              formatDateForChip,
            )}
            onClear={() => setDateRange(null, null)}
            renderPopover={(close) => (
              <DateRangePopover
                initialFrom={dateFromValue}
                initialTo={dateToValue}
                onApply={setDateRange}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Email"
            iconName="email"
            active={!!search.email}
            value={search.email}
            onClear={() => updateSearch({ email: undefined })}
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.email ?? ""}
                onApply={(v) =>
                  updateSearch({
                    email: v.length > 0 ? v : undefined,
                  })
                }
                onClose={close}
                placeholder="Email"
              />
            )}
          />
          <Select2
            label="Price"
            iconName="price"
            active={
              search.total_price_from !== undefined ||
              search.total_price_to !== undefined
            }
            value={formatPriceRangeForChip(
              search.total_price_from,
              search.total_price_to,
            )}
            onClear={() =>
              updateSearch({
                total_price_from: undefined,
                total_price_to: undefined,
              })
            }
            renderPopover={(close) => (
              <PriceRangePopover
                initialFrom={
                  search.total_price_from !== undefined
                    ? String(search.total_price_from)
                    : ""
                }
                initialTo={
                  search.total_price_to !== undefined
                    ? String(search.total_price_to)
                    : ""
                }
                onApply={(from, to) =>
                  updateSearch({
                    total_price_from: from.length > 0 ? from : undefined,
                    total_price_to: to.length > 0 ? to : undefined,
                  })
                }
                onClose={close}
              />
            )}
          />
          <Select2
            label="Currency"
            iconName="currency"
            active={!!selectedCurrencyCode}
            value={currencyChipValue}
            onClear={clearCurrency}
            renderPopover={(close) => (
              <CurrencyFilterPopover
                currencies={currencies}
                initialValue={selectedCurrencyCode}
                onApply={selectCurrency}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Description"
            iconName="description"
            active={!!search.descr}
            value={search.descr}
            onClear={() => updateSearch({ descr: undefined })}
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.descr ?? ""}
                onApply={(v) =>
                  updateSearch({
                    descr: v.length > 0 ? v : undefined,
                  })
                }
                onClose={close}
                placeholder="Description"
              />
            )}
          />
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            Clear all
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, position: "relative", minHeight: 0, mt: 2.5 }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <ReadableDataGrid
            dataTestId="transactions-grid"
            columns={columns}
            rows={rows}
            rowCount={rowCount}
            loading={isLoadingTransactions}
            showAllRows={false}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 50, 100]}
            onRefresh={() => {
              void refetchTransactions();
            }}
            rowNavigation={{
              getUrl: (row) => getTransactionUrl(row),
              onNavigate: (url) => navigate({ to: url }),
            }}
            localeText={{
              noRowsLabel: "No Transactions",
            }}
            handleExport={handleExport}
            isExporting={isExporting}
            exportDialogHeading={exportDialogHeading}
            exportDialogMessage={exportDialogMessage}
            handleCancelExport={handleCancelExport}
          />
        </Box>
      </Box>
    </Stack>
  );
}
